// The two actions a phone draws as a mark instead of a sentence.
//
// A modern phone app does not spend a line of a card on the word "Edit", nor
// the end of a title row on "Add client". It puts a pencil where the thing to
// change is and a plus where the new thing will go, and lets the screen say
// what they are. A desktop has width for the words and a pointer that has to
// be told, so up there both keep their labels exactly as they were.
//
// One button, not two. The label is `sr-only` on a phone rather than removed,
// so the button's accessible name is the same sentence at every width — a
// screen reader, and every test that looks a button up by its name, finds the
// same control whichever side of `md` it is on. The alternative, drawing an
// icon button under `md` and a labelled one over it, puts two buttons with the
// same name in the page at once.
//
// `md` is the app's phone line — the same one the rail, the tab bar and the
// header's back arrow turn on.
//
// The plus can keep a word on a phone where the word is worth the width —
// `phoneLabel="New"` on the two list screens that are nothing but their list,
// where the header row holds the title and this and nothing else. That draws
// the ordinary filled pill instead of the circle below, so the one action on
// the screen reads as one rather than as a mark to work out. Everywhere the
// plus shares its row — the brand tabs, beside a search or a tab strip — the
// circle is still the shape, because there the width is already spoken for.
//
// Not for an empty state. The welcome panels — "Add your first client", "Your
// leads start here" — keep their labelled buttons at every width, and are left
// on plain `Button` for that reason. A mark works when the screen around it
// already says what the thing is: a list of clients with a plus over it can
// only mean one thing. An empty screen says nothing yet, and its one button is
// the sentence telling you what to do with it — a bare plus there is a riddle.

import React from "react";
import Button, { ButtonProps } from "./Button";
import { PencilIcon, PlusIcon } from "./Icons";

export interface IconActionProps
  extends Omit<ButtonProps, "children" | "startIcon" | "endIcon" | "size"> {
  /**
   * What the action is, written out. Shown as the button's label from `md` up
   * and read out at every width — so it is a sentence ("Add client"), not a
   * word the icon already says ("Add").
   */
  label: string;
  /**
   * The one word a phone draws beside the plus instead of the bare circle —
   * "New". Supplying it turns the phone shape into the ordinary filled pill
   * `md` already has, sized to those two marks rather than to the sentence.
   *
   * For the screens where the list is the page and the plus is the only thing
   * on the header row with it: there the pill costs a word of width and buys
   * back a button that says what it does. The circle below stays the default,
   * for the places the action shares its row with something else.
   *
   * The sentence is still the accessible name — `aria-label` carries `label`
   * so the control a screen reader or a test finds is the same one at every
   * width, and the visible "New" is the only thing that changes.
   */
  phoneLabel?: string;
}

/**
 * A 40px circle in pale teal, carrying its mark in the brand colour: the touch
 * target a thumb needs, in the one weight both of these actions wear.
 *
 * Tinted rather than filled. A phone screen can hold both of them at once — a
 * list with a plus over it, a card with a pencil on it — and two filled teal
 * circles would be the two loudest things on a screen that is mostly someone's
 * name and their details. The tint is the pale teal the `outline` variant
 * already hovers to, so nothing new is introduced; the deeper one below is
 * `primary`'s own disabled fill, used here as the pressed state.
 *
 * `!p-0` because the size's own `px-4 py-2` would otherwise stretch the circle
 * into a pill, and `!bg`/`!text` because `primary` is still the variant from
 * `md` up — the one place a caller has to fight it, since on a phone the shape
 * and the weight are the whole point.
 */
const phoneCircle =
  "max-md:h-10 max-md:w-10 max-md:!p-0 max-md:rounded-full max-md:flex-shrink-0 " +
  "max-md:!bg-[#E6FAFB] max-md:!text-[#00C2CB] max-md:hover:!bg-[#D7F9FD]";

/**
 * The pill, at 32px on a phone — the one size in the app that is deliberately
 * under a thumb's 44px, at every phone width.
 *
 * `sm` draws a 44px button below `md`, which is right for something a thumb
 * hunts for in a list of rows. This one is not in a list. It sits on the title
 * row, level with "My clients", and at 44px it was the tallest and heaviest
 * thing on a screen whose heading is 18px — the page read as the button's,
 * not the list's. At 32px it is a trim beside the title, which is what it is:
 * a way in, not the screen's subject.
 *
 * Under 44px and that is the trade, made once and on purpose. It is bought
 * back three ways: the pill is wide, not a 32px square; nothing sits within
 * 12px of it, so there is no neighbour to hit by mistake; and on these two
 * screens the same action has a second, bigger way in further down the page —
 * the empty state's "Add client" and, on a list, the thumb's own reach at the
 * bottom of the screen. The circle above is still 40px, because there it is a
 * bare mark with no word to aim at and no room to be wide.
 *
 * `!` on each because `sm`'s own `px-4 py-3 text-sm` is in the same
 * layer: this is the one place a caller overrules the size it asked for.
 * `text-[13px]/[16px]` spells the line box out rather than leaving it to
 * inherit — an arbitrary font size sets the size and nothing else, and the
 * height here is padding plus that line and nothing more. 8+8+16 = 32.
 */
const phonePill =
  "max-md:!py-2 max-md:!px-3.5 max-md:!text-[13px]/[16px] flex-shrink-0";

/** The label, out of the way on a phone and spelled out from `md` up. */
const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="sr-only md:not-sr-only">{children}</span>
);

/**
 * The way to make a new one of whatever the screen is listing — a plus in the
 * circle above on a phone, the labelled button it has always been over `md`.
 */
export const AddAction: React.FC<IconActionProps> = ({
  label,
  phoneLabel,
  className = "",
  ...rest
}) => (
  <Button
    size="sm"
    // Only when the phone is drawing a word of its own: over `md` the visible
    // label already is the sentence, and a matching `aria-label` would just be
    // a second copy of it to keep in step.
    aria-label={phoneLabel ? label : undefined}
    startIcon={
      <PlusIcon
        className={
          phoneLabel ? "h-3.5 w-3.5 md:h-4 md:w-4" : "h-5 w-5 md:h-4 md:w-4"
        }
      />
    }
    className={`${phoneLabel ? phonePill : phoneCircle} ${className}`}
    {...rest}
  >
    {phoneLabel ? (
      <>
        <span className="md:hidden">{phoneLabel}</span>
        <span className="max-md:hidden">{label}</span>
      </>
    ) : (
      <Label>{label}</Label>
    )}
  </Button>
);

/**
 * The way to change the record a card is showing — a pencil at the card's top
 * right on a phone, the labelled button it has always been over `md`.
 */
export const EditAction: React.FC<IconActionProps> = ({
  label,
  className = "",
  ...rest
}) => (
  <Button
    size="sm"
    startIcon={<PencilIcon className="h-5 w-5 md:hidden" />}
    className={`${phoneCircle} ${className}`}
    {...rest}
  >
    <Label>{label}</Label>
  </Button>
);
